import type { KeyIndicateSyncDataTypeSelection } from './keyIndicateSyncSettingsApi';

export type ApiTenant = {
    id: string;
    name: string;
    customerDbKey: string;
    createUserFromAgent: boolean;
    defaultAgentRoleId: string | null;
};

export type ResponseGetTenants = Record<string, ApiTenant>;

export type Tenant = ApiTenant & { name: string };

export type RequestMigrateCompany = {
    name: string;
    companyId: number;
    configs?: string[];
};

export type ResponseMigrateCompany = ApiTenant & { name: string };

export type UnmigratedCompany = {
    id: number;
    name: string;
};

export type ResponseGetUnmigratedCompanies = UnmigratedCompany[];

export type UnmigratedExternalSystem = {
    systemId: number;
    systemName: string | null;
    companyId: number;
};

export type ResponseGetUnmigratedExternalSystems = UnmigratedExternalSystem[];

export type MigratedTenant = {
    id: string;
    name: string;
    companyId: number;
};

export type ResponseGetMigratedTenants = MigratedTenant[];

export type MigratedExternalSystem = {
    systemId: string;
    systemName: string;
};

export type ResponseGetMigratedExternalSystems = MigratedExternalSystem[];

export type RequestMigrateSystem = {
    tenantId: string;
    keyIndicateSystemId: number;
    indicateSystemId: string | null;
    systemName: string;
    configs?: string[];
    active: boolean;
    dataTypes: KeyIndicateSyncDataTypeSelection[];
};

export type DataSeedingResponse = {
    problems: Record<string, string[]>;
};

export type ApiCompany = {
    companyId: number;
    name: string;
    isInV2: boolean;
    tenantId: string | null;
};

export type ApiRole = {
    id: string;
    name: string;
};

export type ApiTenantRolesResponse = {
    roles: ApiRole[];
};

export type ApiKeyIndicateUser = {
    email: string;
    firstName: string;
    lastName: string;
    keyIndicateRoleId: number;
    keyIndicateRoleName: string;
    echoRoleId: string | null;
    keyIndicateEmployeeId: number;
    echoEmployeeId: string | null;
};

export type KeyIndicateUserWithEchoMatch = {
    keyIndicateUser: ApiKeyIndicateUser;
    echoRole: ApiRole | null;
};

export type ApiKeyIndicateUsersResponse = {
    users: ApiKeyIndicateUser[];
};

export type RequestImportUser = {
    email: string;
    firstName: string;
    lastName: string;
    roleId: string;
    employeeId: string | null;
};

export type RequestImportUsers = {
    tenantId: string;
    users: RequestImportUser[];
};

export type RequestEnqueueCreateUsersFromAgents = {
    tenantId: string;
    agentIds: string[];
    // Overrides the tenant's saved DefaultAgentRoleId for this call - lets an admin send without
    // first saving a default role on the tenant.
    roleId?: string;
};

export type ApiCreateUserFromAgentEnqueueResult = {
    agentId: string;
    status: 'Enqueued' | 'AlreadyQueued' | 'NotEligible' | 'Unknown';
    reason: string | null;
};

export type ApiAgentWithoutUser = {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    externalEmployeeId: string | null;
};

export type ImportTenantConfigOption = {
    name: string;
    isEnabled: boolean;
    isSelected: boolean;
};

export type ResponseGetImportTenantConfigOptions = ImportTenantConfigOption[];

export type RequestImportTenantConfig = {
    tenantId: string;
    configs: string[];
};

export type RequestUpdateOrgTree = {
    tenantId: string;
};

export type ResponseUpdateOrgTree = {
    errors: string[];
};

export type ImportIntegrationConfigOption = {
    name: string;
    isEnabled: boolean;
    isSelected: boolean;
};

export type ResponseGetImportIntegrationConfigOptions = ImportIntegrationConfigOption[];

export type RequestImportIntegrationConfig = {
    tenantId: string;
    externalSystemId: string;
    configs: string[];
};

export type IntegrationConfigFieldDefinition = {
    key: string;
    label: string;
    type: string;
    required: boolean;
    defaultValue: string | null;
    options: string[] | null;
    section: string | null;
};

export type IntegrationSystemOption = {
    systemKey: string;
    systemName: string;
    fields: IntegrationConfigFieldDefinition[];
};

export type ResponseGetIntegrationConfigOptions = IntegrationSystemOption[];

export type IntegrationConfigValues = {
    fieldValues: Record<string, string>;
    storedSecrets: string[];
};

export type RequestSaveIntegrationConfig = {
    tenantId: string;
    systemKey: string;
    fieldValues: Record<string, string>;
};

export type IntegrationConfigSummary = {
    systemKey: string;
    systemName: string;
};

export type ResponseGetIntegrationConfigs = IntegrationConfigSummary[];

export type RequestCreateTenant = {
    name: string;
    dbKey: string;
};

export type ResponseCreateTenant = {
    id: string;
    customerDbKey: string;
    warnings: string[];
};

export type TenantExternalSystem = {
    systemId: string;
    systemName: string;
};

export type RequestSyncRawDataProcessor = {
    tenantId: string;
    externalSystemId: string;
    start: string;
    end: string;
};

export type RequestSyncCoachingData = {
    tenantId: string;
    externalSystemId: string;
    start: string;
    end: string;
    includeProtocols: boolean;
};

export type RequestSyncAiInsightsData = {
    tenantId: string;
    externalSystemId: string;
    start: string;
    end: string;
};

export enum SyncJobStatus {
    Waiting = 0,
    Running = 1,
    Success = 2,
    Failed = 3,
}

export type SyncJob = {
    id: number;
    customerName: string;
    systemName: string;
    rangeStart: string;
    rangeEnd: string;
    dataTypes: string;
    status: SyncJobStatus;
    startedAt: string | null;
    endedAt: string | null;
    attempts: number;
    output: string;
};

export type ApiCreateUserFromAgentJobEntry = {
    jobId: number;
    agentId: string;
    agentName: string;
    status: SyncJobStatus;
    startedAt: string | null;
    endedAt: string | null;
    output: string;
};

export type OneOffJobEntry = {
    id: number;
    tenantId: string;
    tenantName: string;
    status: SyncJobStatus;
    startedAt: string | null;
    endedAt: string | null;
    output: string;
};

export type OneOffJobTriggerResponse = {
    jobIds: number[];
};

export type IntegrationSyncOverviewRow = {
    tenantId: string;
    tenantName: string;
    dataType: string;
    isEnabled: boolean;
    lastSyncedAt: string | null;
};

export type ResponseGetIntegrationOverview = IntegrationSyncOverviewRow[];

export type RequestManualSync = {
    tenantId: string;
    start: string;
    end: string;
    dataTypes: string[];
};

export type ManualSyncResult = {
    id: number;
};

export type RequestTestGenesysConnection = {
    region: string | null;
    clientId: string;
    clientSecret: string;
};

export type ApiTenantUser = {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    role: ApiRole | null;
};

export type ApiTenantUsersEntry = {
    tenantId: string;
    tenantUsers: { users: ApiTenantUser[] };
    problem: string | null;
};

export type ResponseGetTenantUsers = Record<string, ApiTenantUsersEntry>;

export type ApiSysOpUserRow = {
    user: ApiTenantUser;
    emailVerified: boolean;
};

export type ApiSysOpTenantUsersEntry = {
    tenantId: string;
    users: ApiSysOpUserRow[];
    problem: string | null;
};

export type ResponseGetSysOpUsers = Record<string, ApiSysOpTenantUsersEntry>;

export type RequestCreateSysOpUser = {
    tenantId: string;
    email: string;
    firstName: string;
    lastName: string;
    roleId: string;
};

export type RequestChangeUserRole = {
    tenantId: string;
    userId: string;
    roleId: string;
};
